import React, {useState, useEffect} from "react";
import ReactDOM from "react-dom";
import TrixTextEditor from "../ISR/Components/TrixTextEditor";
import {HelpTextData} from "../ISR/Pages/DefineModel/model";
import Dropdown from "Components/DesignSystem/Dropdown/Dropdown";
import Toggle from "Components/DesignSystem/Toggle/Toggle";
import Button from "Components/DesignSystem/Button/Button";
import Textarea from "Components/DesignSystem/Textarea/Textarea";
import FieldTitle from "Components/DesignSystem/FieldTitle/FieldTitle";
import InfoTooltip from "Components/DesignSystem/Tooltip/InfoTooltip";
import Typography from "Components/DesignSystem/Typography/Typography";

type HelpTextProps = {
    id: string;
    savedHelpTextData?: HelpTextData | null | undefined
    selectedItemId: string | null | undefined
    attachmentUrl: string
    showDescription?: boolean
    onDelete?: () => void;
    onClose?: () => void;
    onDone?: (helpTextData: HelpTextData) => void;
    onSubmit?: (helpTextData: HelpTextData) => void;
    onAutoSave?: (helpTextData: HelpTextData) => void;
    /**
     * § 84: a dialog consumer passes its Dialog-footer element here and the
     * action row PORTALS into that band (tinted, top-ruled, pinned under
     * scroll) instead of floating in the content. The state stays in this
     * component — which is why the buttons cannot simply move to the Dialog's
     * `footer` prop — and ISR's inline consumer (`AdditionalData`, no dialog,
     * no action callbacks) is untouched by omitting it.
     */
    actionsContainer?: HTMLElement | null;
}

const looksLikeHtml = (s: string) => /<[a-z][^>]*>[\s\S]*<\/[a-z]/i.test(s);

const stripHtml = (html: string): string => {
    try {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.body.textContent ?? '';
    } catch {
        return html;
    }
};

const HelpText = ({id, showDescription = false, savedHelpTextData, selectedItemId, attachmentUrl, onDelete, onClose, onDone, onSubmit, onAutoSave, actionsContainer}: HelpTextProps) => {

    const defaultInfo = {
        info: '',
        infoHtml: '',
        display: 'outside',
        trigger: 'hover',
        size: 'auto',
        openInNewWindow: false
    }

    const [helpTextData, setHelpTextData] = useState<any>(defaultInfo);

    const [loading, setLoading] = useState<boolean>(true);

    const updateInfo = (html: string) => {
        const updated = { ...helpTextData, info: stripHtml(html), infoHtml: html };
        setHelpTextData(updated);
        if (onAutoSave) onAutoSave(updated);
    }

    useEffect(() => {
        if(savedHelpTextData) {
            // Detect old-format records where info contains Trix HTML instead of plain text.
            // Migrate local state by stripping HTML into info and moving it to infoHtml so the
            // Trix editor restores formatting correctly. Do NOT call onAutoSave here: the
            // migration fires on every scroll/field-select for un-migrated fields, which would
            // trigger a spurious save API call and flip hasUnpromotedChanges even when the user
            // made no edit. The backend migration in manage_isr_delta_states handles persisting
            // the correct format to the live ISR database on the next promotion.
            const needsMigration = !savedHelpTextData.infoHtml && looksLikeHtml(savedHelpTextData.info ?? '');
            const infoHtml = savedHelpTextData.infoHtml ?? (needsMigration ? savedHelpTextData.info : '') ?? '';
            const info = needsMigration ? stripHtml(savedHelpTextData.info ?? '') : (savedHelpTextData.info ?? '');
            const newData = {
                info,
                infoHtml,
                display: savedHelpTextData.display ?? 'outside',
                trigger: savedHelpTextData.trigger ?? 'hover',
                size: savedHelpTextData.size ?? 'auto',
                openInNewWindow: savedHelpTextData.openInNewWindow ?? false,
                description: savedHelpTextData.description ?? ''
            };
            setHelpTextData(newData);
        } else {
            setHelpTextData(defaultInfo);
        }
        setLoading(false)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [savedHelpTextData]);

    const handleValueChange = (fieldName: string, value: string) => {
        const temp = { ...helpTextData, [fieldName]: value };
        setHelpTextData(temp);
        if (onAutoSave) onAutoSave(temp);
    }

    const handleOpenInNewWindowChange = (value: boolean) => {
        const temp = { ...helpTextData, openInNewWindow: value };
        setHelpTextData(temp);
        if (onAutoSave) onAutoSave(temp);
    };

    /* DS Dropdown (§ 84). Values stay the lowercase strings the backend knows
       ('hover', 'auto', …); only the option LABELS are capitalized — the legacy
       markup faked this with `tw-capitalize`. Each dropdown finally gets its
       own testid; both used to share `list-helptext-select-1` (the okf/list
       § 4 known gap). */
    const dropDownUi = (title: string, hint: string, values: string[], defaultValue: string, testid: string) => {
        const fieldName = title.toLowerCase();
        return <div className='tw-me-[16px]'>
            <div className="tw-flex tw-items-center tw-gap-1 tw-mb-2">
                <FieldTitle label={title} />
                <InfoTooltip message={hint} label={`About ${title}`} />
            </div>
            <Dropdown
                label=""
                aria-label={title}
                options={values.map((item) => ({ value: item, label: item.charAt(0).toUpperCase() + item.slice(1) }))}
                value={helpTextData[fieldName] ?? defaultValue}
                onChange={(value) => handleValueChange(fieldName, String(value))}
                className="tw-w-[153px]"
                data-testid={testid}
            />
        </div>;
    }

    /* Into the dialog's footer band when a container is supplied; inline otherwise. */
    const renderActions = (row: React.ReactNode) =>
        actionsContainer ? ReactDOM.createPortal(row, actionsContainer) : row;

    const triggerHint = 'Option 1: Hover - This selection makes on-mouse hover over \'i\' icon to view the info tool tip\n\nOption 2: Click - In this selection user need to click \'i\' icon to view the info tool tip';
    const sizeHint = 'Option 1: Auto - This selection automatically fits the tool tip window as per the help text content. User can drag the window to any size.\n' +
        '\n' +
        'Option 2: Small - This selection provides the small size window, user can drag the window to view the help text content.\n' +
        '\n' +
        'Option 3: Medium - This selection provides the medium size window, user can drag the window to view the help text content.\n' +
        '\n' +
        'Option 4: Large - This selection provides the large size window, user can drag the window to view the help text content.';
    return(
        <div id={id}>
            {showDescription ? (
                <Textarea
                    label="Description"
                    id={`${id}-description-input`}
                    className="tw-mt-3"
                    value={helpTextData?.description ?? ''}
                    onChange={(value) => handleValueChange('description', value)}
                    placeholder={'Enter the Description'}
                    data-testid="list-helptext-textarea-2"
                />
            ) : null}

            {showDescription ? (
                <Typography variant="sub-title-label" as="div" className="tw-mt-3 tw-text-ink-secondary">
                    Help Text
                </Typography>
            ) : null}

            <div id={`${id}-editor-container`} className="tw-mt-3">
                {!loading && 
                    <TrixTextEditor 
                        id={`${id}-text-editor`}
                        updateInfo={updateInfo} 
                        value={helpTextData?.infoHtml ?? helpTextData?.info ?? "<div></div>"}
                        selectedItemId={selectedItemId}
                        attachmentUrl={attachmentUrl}
                    />
                }
            </div>

            <div className='tw-flex tw-items-end tw-mb-[32px] tw-mt-3'>
                {dropDownUi('Trigger', triggerHint, ['hover', 'click'], 'hover', 'list-helptext-trigger-select')}
                {dropDownUi('Size', sizeHint, ['auto', 'small', 'medium', 'large'], 'auto', 'list-helptext-size-select')}
                <div className="tw-w-auto">
                    <div className="tw-flex tw-items-center tw-gap-1 tw-mb-2">
                        <FieldTitle label="Auto Pin" />
                        <InfoTooltip
                            message={'When enabled, the info tooltip automatically pins and stays visible.'}
                            label="About Auto Pin"
                        />
                    </div>
                    <Toggle
                        label=""
                        aria-label="Auto Pin"
                        id={`${id}-new-window-toggle`}
                        checked={!!helpTextData['openInNewWindow']}
                        onChange={(checked) => handleOpenInNewWindowChange(checked)}
                        data-testid="list-helptext-autopin-toggle"
                    />
                </div>
            </div>

            {/* DS Buttons at the default md — dialog footers are md across the DS
                (§ 35/§ 76); the retired `samll-*`/`small-*` classes were the legacy
                small family. Delete is the § 84 danger variant. */}
            {renderActions(
            <div id={`${id}-actions`} className={`${actionsContainer ? '' : 'tw-my-4 '}tw-flex tw-justify-between tw-w-full`}>
                {onDelete ?
                    <Button data-testid="list-helptext-button-3"
                        id={`${id}-delete-btn`}
                        variant="danger"
                        onClick={onDelete}
                        disabled={!savedHelpTextData}
                    >
                        Delete
                    </Button>
                : null}
                {onDone ?
                    <Button data-testid="list-helptext-button-4"
                        id={`${id}-done-btn`}
                        variant="primary"
                        onClick={() => onDone(helpTextData)}
                    >
                        Done
                    </Button>
                : null}
                <div id={`${id}-right-actions`} className='tw-flex tw-gap-2 tw-justify-end'>
                    {onClose ?
                        <Button data-testid="list-helptext-button-5"
                            id={`${id}-cancel-btn`}
                            variant="secondary"
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                    : null}
                    {onSubmit ?
                        <Button data-testid="list-helptext-button-6"
                            id={`${id}-save-btn`}
                            variant="primary"
                            onClick={() => onSubmit(helpTextData)}
                        >
                            Save
                        </Button>
                    : null}
                </div>
            </div>
            )}
        </div>
    );
}

export default HelpText;